import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 50;

/** HTML month input (YYYY-MM) -> qt_ym (YYYYMM) */
function monthInputToQtYm(monthStr) {
  if (!monthStr || typeof monthStr !== 'string') return null;
  const compact = monthStr.replace(/-/g, '');
  return compact.length === 6 ? compact : null;
}

function getNextMonthStartYmd(qtYm) {
  if (!qtYm || qtYm.length !== 6) return null;
  const year = Number(qtYm.slice(0, 4));
  const month = Number(qtYm.slice(4, 6));
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  const next = new Date(year, month, 1);
  return `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}01`;
}

function getCurrentMonthInput() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** ILIKE 패턴용: 와일드카드·or() 구분자 쉼표 제거 */
function sanitizeTextSearch(raw) {
  return raw.trim().replace(/[%_,]/g, '');
}

function DailyScriptureList() {
  const [scriptures, setScriptures] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const observerTarget = useRef(null);

  const [searchYm, setSearchYm] = useState(() => getCurrentMonthInput());
  const [searchText, setSearchText] = useState('');
  /** 비우면 전체, 값이 있으면 language 컬럼과 정확히 일치 */
  const [languageFilter, setLanguageFilter] = useState('');
  const [currentFilters, setCurrentFilters] = useState({
    searchMonth: '',
    textRaw: '',
    langRaw: ''
  });

  const buildQuery = (searchMonth, textRaw, langRaw) => {
    let query = supabase.from('daily_scriptures').select('*', { count: 'exact' });

    const qtYm = monthInputToQtYm(searchMonth);
    if (qtYm) {
      const monthStart = `${qtYm}01`;
      const nextMonthStart = getNextMonthStartYmd(qtYm);
      query = query.gte('qt_ymd', monthStart);
      if (nextMonthStart) query = query.lt('qt_ymd', nextMonthStart);
    }

    const lang = typeof langRaw === 'string' ? langRaw.trim() : '';
    if (lang) {
      query = query.eq('language', lang);
    }

    const text = sanitizeTextSearch(textRaw);
    if (text) {
      const p = `%${text}%`;
      query = query.or(
        `title.ilike.${p},reference.ilike.${p},commentary.ilike.${p},question.ilike.${p},dictionary.ilike.${p},daily_insight.ilike.${p},language.ilike.${p},qt_ymd.ilike.${p}`
      );
    }

    return query.order('date', { ascending: false });
  };

  const runFetch = async (searchMonth, textRaw, langRaw) => {
    setLoading(true);
    setOffset(0);
    setHasMore(true);
    setAllData([]);
    setScriptures([]);
    setCurrentFilters({ searchMonth, textRaw, langRaw });
    
    try {
      const query = buildQuery(searchMonth, textRaw, langRaw);
      const { data, error, count } = await query.range(0, PAGE_SIZE - 1);
      
      if (error) throw error;
      
      setAllData(data || []);
      setScriptures(data || []);
      setOffset(PAGE_SIZE);
      setHasMore((data?.length || 0) >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setScriptures([]);
      setAllData([]);
      alert(e.message || '목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const query = buildQuery(currentFilters.searchMonth, currentFilters.textRaw, currentFilters.langRaw);
      const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);
      
      if (error) throw error;
      
      const newData = data || [];
      setScriptures(prev => [...prev, ...newData]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newData.length >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    runFetch(searchYm, '', '');
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, offset, currentFilters]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runFetch(searchYm, searchText, languageFilter);
  };

  const handleResetSearch = () => {
    setSearchYm('');
    setSearchText('');
    setLanguageFilter('');
    runFetch('', '', '');
  };

  const handleActiveChange = async (e, scriptureToUpdate) => {
    e.stopPropagation();
    const newIsActive = e.target.checked ? 'Y' : 'N';

    const originalScriptures = [...scriptures];
    setScriptures((prevScriptures) =>
      prevScriptures.map((s) => (s.id === scriptureToUpdate.id ? { ...s, is_active: newIsActive } : s))
    );
    const { error } = await supabase
      .from('daily_scriptures')
      .update({ is_active: newIsActive })
      .eq('id', scriptureToUpdate.id);

    if (error) {
      console.error('Error updating is_active:', error);
      setScriptures(originalScriptures);
      alert('활성 상태 업데이트에 실패했습니다.');
    } else {
      alert('활성 상태가 성공적으로 업데이트되었습니다.');
    }
  };

  const handleCheckboxChange = (id, event) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(scriptures.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedIds.length}개의 큐티 본문을 삭제하시겠습니까?\n관련된 성경 구절(scripture_verses)도 함께 삭제됩니다.`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      const { error } = await supabase.from('daily_scriptures').delete().in('id', selectedIds);

      if (error) throw error;

      alert('삭제가 완료되었습니다.');
      setSelectedIds([]);
      await runFetch(searchYm, searchText, languageFilter);
    } catch (error) {
      console.error('삭제 오류:', error);
      alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleRowClick = (item, event) => {
    if (event && event.target.type === 'checkbox') {
      return;
    }
    navigate(`/qt/DailyScriptureForm/${item.id}`);
  };

  const handleNewClick = () => {
    navigate('/qt/DailyScriptureForm/new');
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex align-items-center justify-content-between">
        <h6 className="m-0 font-weight-bold text-primary">큐티 본문 목록</h6>
        <div>
          <button
            className="btn btn-success btn-sm mr-2"
            onClick={handleNewClick}
            disabled={loading}
          >
            신규 입력
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDelete}
            disabled={!selectedIds.length || deleting || loading}
          >
            {deleting ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
      <div className="card-body">
        <form className="mb-4 p-3 bg-light rounded border" onSubmit={handleSearchSubmit}>
          <div className="form-row align-items-end">
            <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
              <label htmlFor="qt-search-ym" className="small font-weight-bold mb-1">
                검색년월
              </label>
              <input
                id="qt-search-ym"
                type="month"
                className="form-control form-control-sm"
                value={searchYm}
                onChange={(e) => setSearchYm(e.target.value)}
              />
            </div>
            <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
              <label htmlFor="qt-language" className="small font-weight-bold mb-1">
                언어 (language)
              </label>
              <input
                id="qt-language"
                type="text"
                className="form-control form-control-sm"
                list="qt-language-list"
                placeholder="전체"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                autoComplete="off"
              />
              <datalist id="qt-language-list">
                <option value="ko" />
                <option value="en" />
                <option value="ja" />
                <option value="zh" />
              </datalist>
            </div>
            <div className="form-group col-md-6 col-lg-4 mb-2 mb-md-0">
              <label htmlFor="qt-text-search" className="small font-weight-bold mb-1">
                텍스트 검색
              </label>
              <input
                id="qt-text-search"
                type="text"
                className="form-control form-control-sm"
                placeholder="제목, 성경구절, 해설, 질문, 큐티일자 등"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="form-group col-md-12 col-lg-2 mb-0 d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                검색
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleResetSearch}
                disabled={loading}
              >
                조건 초기화
              </button>
            </div>
          </div>
          {/* <small className="text-muted d-block mt-2 mb-0">
            기간은 (YYYYMMDD) 기준입니다. 언어는 <code>language</code> 컬럼과 <strong>정확히 일치</strong>할 때만 조회됩니다(비우면 전체). 텍스트는 제목·참조·해설·질문·사전·묵상·언어·qt_ymd에서 부분 일치(대소문자 무시)합니다.
          </small> */}
        </form>

        {loading ? (
          <div className="text-muted py-3">조회 중...</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered" width="100%" cellSpacing="0">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.length === scriptures.length && scriptures.length > 0}
                        onChange={(event) => handleSelectAll(event.target.checked)}
                      />
                    </th>
                    <th>날짜</th>
                    <th>제목</th>
                    <th>언어</th>
                    <th>성경구절</th>
                    <th>활성</th>
                  </tr>
                </thead>
                <tbody>
                  {scriptures.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        조회된 데이터가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    scriptures.map((item) => (
                      <tr
                        key={item.id}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleRowClick(item, e)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={(e) => handleCheckboxChange(item.id, e)}
                          />
                        </td>
                        <td>{item.date}</td>
                        <td>{item.title}</td>
                        <td>{item.language || '-'}</td>
                        <td>{item.reference}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={item.is_active === 'Y'}
                            onChange={(e) => handleActiveChange(e, item)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* 스크롤 페이징 트리거 */}
            <div ref={observerTarget} className="text-center py-4">
              {loadingMore && (
                <div className="text-muted">
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  더 불러오는 중...
                </div>
              )}
              {!hasMore && scriptures.length > 0 && (
                <small className="text-muted">모든 데이터를 불러왔습니다.</small>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DailyScriptureList;
